import React, { CSSProperties, ChangeEvent } from "react";
import * as Sentry from "@sentry/react";

export interface IChoice {
    value: string
    label?: string
    enabled: boolean
};


interface IDropDownMenu {
    menuName: string
    defaultVal?: Partial<IChoice>
    required: boolean
    choices: IChoice[]
    onSelect?: Function
    testId?: string
    height?: string
    disabled?: boolean
    // Opt-in controlled mode. Pass this instead of defaultVal when the selection can change
    // after mount - defaultVal is applied by React only on the first render.
    value?: string
};


// Generic search function that is expected to always find at most one item within the given array of options.
export const findOptionFromList = (optionValue: string | number, choices: IChoice[]): IChoice =>
    choices.filter(option => option.value === String(optionValue))[0];


export default function DropDownMenu({ menuName, defaultVal, required, choices, onSelect, testId, height, disabled, value }: IDropDownMenu) {

    const onSelectOption = (event: ChangeEvent<HTMLSelectElement>) => {
        event.preventDefault();
        onSelect && onSelect(event);
    };

    const dropDownListStyles: CSSProperties = {
        height: height ? height : '5vh',
        border: '1px solid #C4C4C4',
        borderRadius: '10px',
        minWidth: '50%',
        marginTop: '-1px',
        marginLeft: '-1px',
    };

    const selectedValue = value != null ? value : defaultVal?.value;
    if (selectedValue && !choices.map(item => item.value).includes(selectedValue)) {
        Sentry.addBreadcrumb({
            category: 'breadcrumb_change',
            message: `'Default value is not within the choices values'`,
            data: {
                value: value != null ? value : defaultVal,
            },
        });
    }

    return (
        <select
            style={dropDownListStyles}
            name={menuName}
            required={required}
            value={value}
            defaultValue={value != null ? undefined : (defaultVal != null ? defaultVal.value : undefined)}
            onChange={event => onSelectOption(event)}
            data-testid={testId || 'drop-down-menu-testid'}
            disabled={disabled}
        >
            {/* If there's no default or controlled value provided, then add an empty option. The ==null returns True also for undefined. */}
            {defaultVal == null && value == null && <option />}

            {choices.map((item: IChoice, index: number) => {
                // Ensure 'enabled' property exists and is true if not present or null
                if (item.enabled == null) {
                    item.enabled = true;
                }
                return (
                    <option
                        key={`dropDownList-${index}`}
                        value={item.value}
                        disabled={!item.enabled}
                    >
                        {/* TODO: handle opacity when scrolling up, the disabled dropdown comes up */}
                        {item.label ? item.label : item.value}
                    </option>
                );
            })}

        </select>
    );
};
